import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import AuthenticatedImage from "@/components/AuthenticatedImage";
import { INDIA_STATES } from "@/lib/indiaStates";
import { Building, Plus, Loader2, Pencil, X } from "lucide-react";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

function slugify(name) {
  return (name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 49);
}

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ id: "", name: "", legal_name: "", state: "" });
  const [idTouched, setIdTouched] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/companies")
      .then((r) => setCompanies(r.data || []))
      .catch(() => setCompanies([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onNameChange = (name) => {
    setForm((f) => ({
      ...f,
      name,
      id: idTouched ? f.id : slugify(name),
    }));
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setErr("");
    if (!(form.state || "").trim()) {
      setErr("Company State is required (Professional Tax / legal entity).");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        id: form.id.trim(),
        name: form.name.trim(),
        legal_name: form.legal_name.trim() || form.name.trim(),
        state: form.state.trim(),
      };
      await api.post("/companies", payload);
      setShowAdd(false);
      setForm({ id: "", name: "", legal_name: "", state: "" });
      setIdTouched(false);
      load();
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(", ") : "Failed to create company");
    } finally {
      setCreating(false);
    }
  };

  return (
    <AdminLayout title="Companies" breadcrumbs={["Home", "Organization", "Companies"]}>
      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Building className="h-5 w-5 text-[#226DB4]" /> Legal Entities
          </h3>
          <button
            type="button"
            onClick={() => {
              setShowAdd(true);
              setErr("");
            }}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium"
          >
            <Plus className="h-4 w-4" /> Add Company
          </button>
        </div>

        <p className="px-5 pt-4 text-sm text-slate-600">
          Each company has a stable <strong>ID</strong> used for upload folders on disk:{" "}
          <code className="text-xs bg-slate-100 px-1 rounded">companies/&#123;id&#125;/employees/…</code>
        </p>

        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin inline-block" />
          </div>
        ) : companies.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No companies yet. Add your first legal entity.</div>
        ) : (
          <div className="overflow-x-auto p-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="pb-2 pr-4">Logo</th>
                  <th className="pb-2 pr-4">Display Name</th>
                  <th className="pb-2 pr-4">Company ID</th>
                  <th className="pb-2 pr-4">City</th>
                  <th className="pb-2 pr-4">GSTIN</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                    <td className="py-3 pr-4">
                      <div className="h-10 w-14 rounded border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                        {c.logo_url ? (
                          <AuthenticatedImage
                            src={c.logo_url}
                            alt=""
                            className="h-full w-full object-contain"
                            fallback={<Building className="h-4 w-4 text-slate-300" />}
                          />
                        ) : (
                          <Building className="h-4 w-4 text-slate-300" />
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-4 font-medium text-slate-800">{c.name || "—"}</td>
                    <td className="py-3 pr-4">
                      <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{c.id}</code>
                    </td>
                    <td className="py-3 pr-4 text-slate-600">{c.city || "—"}</td>
                    <td className="py-3 pr-4 text-slate-600">{c.gstin || "—"}</td>
                    <td className="py-3">
                      <Link
                        to={`/companies/${c.id}`}
                        className="inline-flex items-center gap-1 text-[#226DB4] hover:underline"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form
            onSubmit={onCreate}
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800">Add Company</h3>
              <button type="button" onClick={() => setShowAdd(false)} className="p-1 text-slate-400 hover:bg-slate-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            {err && <p className="text-sm text-rose-600">{err}</p>}
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Display Name *</label>
              <input
                className={inputCls}
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Spherical Insights Pvt Ltd"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Company ID *</label>
              <input
                className={inputCls}
                value={form.id}
                onChange={(e) => {
                  setIdTouched(true);
                  setForm((f) => ({ ...f, id: e.target.value.toLowerCase() }));
                }}
                placeholder="spherical-insights"
                pattern="[a-z][a-z0-9\-]{1,48}"
                title="Lowercase letters, digits, hyphens; start with a letter"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Folder: <code>companies/{form.id || "your-id"}/</code> — cannot be changed later.
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Legal Name</label>
              <input
                className={inputCls}
                value={form.legal_name}
                onChange={(e) => setForm((f) => ({ ...f, legal_name: e.target.value }))}
                placeholder="Optional — defaults to display name"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">State *</label>
              <select
                className={inputCls}
                value={form.state}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                required
              >
                <option value="">Select state…</option>
                {INDIA_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Used for Professional Tax when employees work from Head Office.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#226DB4] text-white rounded font-medium disabled:opacity-70"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Create
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
